import { useEffect, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useJobStore } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { queryClient } from "../../../shared/api/queryClient";
import { jobKeys, jobStatusQuery } from "../api/jobQueries";
import { jobFailureMessage } from "../model/applyJobStatus";

/**
 * THE status transport: one jobStatusQuery per active registry job, folding
 * snapshots back into the registry. Replaces the old setInterval machinery.
 *
 * Must be mounted exactly once — it lives at the top of useJobMonitorState,
 * which is itself single-mounted in MainComponent. Pause/resume semantics
 * come from the query layer: the poll predicate idles AWAITING_INPUT jobs,
 * the window-focus listener below does the one cross-device check on
 * refocus, and markJobResumed resets the job's query for an immediate tick.
 *
 * Failure policy (deliberate change from the old loop): only hard HTTP
 * errors mark a job FAILED — network blips keep polling.
 */
export function useActiveJobStatusSync(): void {
  const jobs = useJobStore((state) => state.jobs);
  const activeJobs = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.status === ReportJobStatus.PENDING ||
          job.status === ReportJobStatus.AWAITING_INPUT ||
          job.status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION,
      ),
    [jobs],
  );

  const statusQueries = useQueries({
    queries: activeJobs.map((job) =>
      jobStatusQuery(job.id, { resumedAt: job.resumedAt }),
    ),
  });

  useEffect(() => {
    const store = useJobStore.getState();
    statusQueries.forEach((query, index) => {
      const job = activeJobs[index];
      if (!job) return;
      // The error branch must win: react-query's error dispatch RETAINS the
      // previous data, so once any snapshot exists a data-first check would
      // shadow hard HTTP errors forever and the job could never fail. A
      // success dispatch clears the error, so a non-null error always
      // reflects the most recent completed fetch.
      if (query.error && isAxiosError(query.error) && query.error.response) {
        store.failJob(job.id, jobFailureMessage(query.error));
      } else if (query.data) {
        store.applyStatusSnapshot(job.id, query.data);
      }
    });
    // statusQueries is a fresh array every render; the registry writes are
    // idempotent (identity-returning reducer), so re-running is free.
  }, [statusQueries, activeJobs]);

  // Cross-device resume: the old code re-kicked polling on window "focus".
  // refetchOnWindowFocus cannot replace that — v5's focusManager listens only
  // to visibilitychange, which does NOT fire when the window regains OS focus
  // while the tab stays visible (the exact other-device-approval scenario).
  // Scoped to job-status queries; the app-wide refetchOnWindowFocus: false
  // policy is untouched.
  useEffect(() => {
    const onFocus = () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.statuses() });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
}
